import type { SxProps, Theme } from '@mui/material/styles';

/**
 * The sidebar Drawer's styles.
 *
 * Docked (desktop), MUI's paper is position: fixed with height: 100%, and the
 * Drawer's own root is an empty spacer. Fixed is measured against the viewport,
 * so on a page taller than the window the dark panel stopped at the window's
 * height in any full-page capture, with the page carrying on beside it. Here
 * the root stretches the full height of the shell and carries the sidebar
 * colour, and the paper sticks to the viewport inside it: the panel always
 * reaches the bottom of the page, and the menu stays in view while scrolling.
 *
 * Temporary (phones and tablets), the paper is an overlay and keeps MUI's
 * fixed position and the theme's overlay shadow.
 */
export function drawerSx({ docked, width, bannerHeight }: { docked: boolean; width: number; bannerHeight: number }): SxProps<Theme> {
  return {
    width,
    flexShrink: 0,
    ...(docked && { alignSelf: 'stretch', background: 'var(--sidebar-bg)' }),
    '& .MuiDrawer-paper': {
      width,
      boxSizing: 'border-box',
      // Collapse animates the width over --dur-slow (spec 4.1); reduced motion snaps it (index.css).
      transition: 'width var(--dur-slow) var(--ease-standard), top var(--dur-slow) var(--ease-standard), height var(--dur-slow) var(--ease-standard)',
      overflow: 'hidden',
      top: bannerHeight,
      ...(docked
        ? {
          position: 'sticky',
          height: bannerHeight ? `calc(100dvh - ${bannerHeight}px)` : '100dvh',
          // A panel in the page, not something floating over it (DESIGN_SYSTEM.md: overlays only).
          boxShadow: 'none',
        }
        : {
          height: bannerHeight ? `calc(100% - ${bannerHeight}px)` : '100%',
          // The phone drawer is a panel that slides over the page: rounded on its open edge.
          maxWidth: 'calc(100vw - 48px)',
          borderRadius: '0 var(--radius-tile) var(--radius-tile) 0',
          background: 'var(--color-surface-inverse)',
        }),
    },
  };
}
